import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Mail, Server, Shield, Send, Loader2, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testingGmail, setTestingGmail] = useState(false);
  const [activeTab, setActiveTab] = useState('smtp');
  
  const [formData, setFormData] = useState({
    active_provider: 'smtp',
    // SMTP
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    sender_email: '',
    sender_name: 'İSG Permit',
    use_tls: true,
    // Gmail
    gmail_email: '',
    gmail_app_password: '',
    gmail_sender_name: 'İSG Permit'
  });
  
  const [hasExistingSettings, setHasExistingSettings] = useState(false);
  const [savedPw, setSavedPw] = useState({ smtp: false, gmail: false });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/email-settings`);
      if (response.data) {
        setFormData({
          active_provider: response.data.active_provider || 'smtp',
          // SMTP
          smtp_host: response.data.smtp_host || '',
          smtp_port: response.data.smtp_port || 587,
          smtp_user: response.data.smtp_user || '',
          smtp_password: '',
          sender_email: response.data.sender_email || '',
          sender_name: response.data.sender_name || 'İSG Permit',
          use_tls: response.data.use_tls !== false,
          // Gmail
          gmail_email: response.data.gmail_email || '',
          gmail_app_password: '',
          gmail_sender_name: response.data.gmail_sender_name || 'İSG Permit'
        });
        setActiveTab(response.data.active_provider || 'smtp');
        setHasExistingSettings(!!response.data.configured);
        setSavedPw({ smtp: !!response.data.has_smtp_password, gmail: !!response.data.has_gmail_password });
      }
    } catch (error) {
      // Ayar yoksa sunucu hata değil boş kayıt döner; buraya düşen gerçek bir hatadır
      console.error('E-posta ayarları yüklenemedi', error?.response?.status || error?.message);
      toast.error('E-posta ayarları yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Aktif provider için şifre kontrolü
    if (formData.active_provider === 'smtp') {
      if (!savedPw.smtp && !formData.smtp_password) {
        toast.error('SMTP şifresi zorunludur');
        return;
      }
      if (!formData.smtp_host || !formData.smtp_user) {
        toast.error('SMTP sunucu ve kullanıcı adı zorunludur');
        return;
      }
    } else if (formData.active_provider === 'gmail') {
      if (!savedPw.gmail && !formData.gmail_app_password) {
        toast.error('Gmail uygulama şifresi zorunludur');
        return;
      }
      if (!formData.gmail_email) {
        toast.error('Gmail adresi zorunludur');
        return;
      }
    }
    
    setSaving(true);
    try {
      const dataToSend = { ...formData };
      
      // Boş şifreleri temizle (mevcut ayarlar varsa)
      if (hasExistingSettings) {
        if (!dataToSend.smtp_password) delete dataToSend.smtp_password;
        if (!dataToSend.gmail_app_password) delete dataToSend.gmail_app_password;
      }
      
      const res = await axios.post(`${API}/email-settings`, dataToSend);
      toast.success('E-posta ayarları kaydedildi');
      setHasExistingSettings(true);
      setSavedPw({ smtp: !!res.data.has_smtp_password, gmail: !!res.data.has_gmail_password });
      setFormData(prev => ({ ...prev, smtp_password: '', gmail_app_password: '' }));
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Ayarlar kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (provider) => {
    if (!hasExistingSettings) {
      toast.error('Önce ayarları kaydetmeniz gerekmektedir');
      return;
    }
    
    if (provider === 'smtp') {
      setTestingSmtp(true);
    } else {
      setTestingGmail(true);
    }
    
    try {
      const response = await axios.post(`${API}/email-settings/test-provider/${provider}`);
      toast.success(response.data.message || 'Bağlantı başarılı!', { duration: 8000 });
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Bağlantı hatası', { duration: 10000 });
    } finally {
      if (provider === 'smtp') {
        setTestingSmtp(false);
      } else {
        setTestingGmail(false);
      }
    }
  };

  const handleProviderChange = (value) => {
    setFormData({ ...formData, active_provider: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="email-settings-page" className="space-y-6 max-w-3xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">E-posta Ayarları</h1>
        <p className="text-slate-500 mt-1">E-posta gönderimi için SMTP veya Gmail ayarlarınızı yapılandırın</p>
      </div>

      {/* Active Provider Selection */}
      <Card className="border-slate-200 bg-gradient-to-r from-slate-50 to-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="h-5 w-5 text-slate-600" />
            Aktif E-posta Servisi
          </CardTitle>
          <CardDescription>
            E-postalar aşağıda seçili olan servis üzerinden gönderilecektir
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup 
            value={formData.active_provider} 
            onValueChange={handleProviderChange}
            className="flex gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="smtp" id="provider-smtp" />
              <Label htmlFor="provider-smtp" className="flex items-center gap-2 cursor-pointer">
                <Server className="h-4 w-4" />
                Özel SMTP
                {formData.active_provider === 'smtp' && (
                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">Aktif</Badge>
                )}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="gmail" id="provider-gmail" />
              <Label htmlFor="provider-gmail" className="flex items-center gap-2 cursor-pointer">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                </svg>
                Gmail
                {formData.active_provider === 'gmail' && (
                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">Aktif</Badge>
                )}
              </Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="smtp" className="flex items-center gap-2">
              <Server className="h-4 w-4" />
              Özel SMTP
            </TabsTrigger>
            <TabsTrigger value="gmail" className="flex items-center gap-2">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
              </svg>
              Gmail
            </TabsTrigger>
          </TabsList>

          {/* SMTP Tab */}
          <TabsContent value="smtp" className="space-y-4 mt-4">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Server className="h-5 w-5" />
                  SMTP Sunucu Ayarları
                </CardTitle>
                <CardDescription>
                  Kendi SMTP sunucunuzun bilgilerini girin (örn: mail.firmaadi.com)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="smtp_host">SMTP Sunucu Adresi</Label>
                    <Input
                      id="smtp_host"
                      value={formData.smtp_host}
                      onChange={(e) => setFormData({ ...formData, smtp_host: e.target.value })}
                      placeholder="smtp.example.com"
                      data-testid="smtp-host-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp_port">Port</Label>
                    <Input
                      id="smtp_port"
                      type="number"
                      value={formData.smtp_port}
                      onChange={(e) => setFormData({ ...formData, smtp_port: parseInt(e.target.value) })}
                      placeholder="587"
                      data-testid="smtp-port-input"
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-slate-500" />
                    <Label htmlFor="use_tls" className="font-normal">TLS/STARTTLS Kullan</Label>
                  </div>
                  <Switch
                    id="use_tls"
                    checked={formData.use_tls}
                    onCheckedChange={(checked) => setFormData({ ...formData, use_tls: checked })}
                    data-testid="use-tls-switch"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="smtp_user">Kullanıcı Adı</Label>
                    <Input
                      id="smtp_user"
                      value={formData.smtp_user}
                      onChange={(e) => setFormData({ ...formData, smtp_user: e.target.value })}
                      placeholder="user@example.com"
                      data-testid="smtp-user-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp_password">
                      Şifre
                      {true && (
                        <span className={`text-xs ml-2 ${savedPw.smtp ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="smtp-password-state">{savedPw.smtp ? '(Kayıtlı)' : '(Kayıtlı şifre yok – girip kaydedin)'}</span>
                      )}
                    </Label>
                    <Input
                      id="smtp_password"
                      type="password"
                      value={formData.smtp_password}
                      onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
                      placeholder={savedPw.smtp ? 'Kayıtlı – değiştirmek için yeni şifre yazın' : '••••••••'}
                      data-testid="smtp-password-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                  <div className="space-y-2">
                    <Label htmlFor="sender_email">Gönderici E-posta</Label>
                    <Input
                      id="sender_email"
                      type="email"
                      value={formData.sender_email}
                      onChange={(e) => setFormData({ ...formData, sender_email: e.target.value })}
                      placeholder="noreply@example.com"
                      data-testid="sender-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sender_name">Gönderici Adı <span className="font-normal text-slate-400">(gelen kutusunda görünür; boşsa "İSG Permit")</span></Label>
                    <Input
                      id="sender_name"
                      value={formData.sender_name}
                      onChange={(e) => setFormData({ ...formData, sender_name: e.target.value })}
                      placeholder="İSG Permit"
                      data-testid="sender-name-input"
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => handleTestConnection('smtp')}
                    disabled={testingSmtp || !hasExistingSettings}
                    data-testid="test-smtp-btn"
                  >
                    {testingSmtp ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Test Ediliyor...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        SMTP Bağlantısını Test Et
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Gmail Tab */}
          <TabsContent value="gmail" className="space-y-4 mt-4">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                  </svg>
                  Gmail Ayarları
                </CardTitle>
                <CardDescription>
                  Gmail hesabınızla e-posta göndermek için uygulama şifresi kullanın
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Info Box */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-2">Gmail Uygulama Şifresi Nasıl Alınır?</p>
                      <ol className="list-decimal list-inside space-y-1 text-blue-700">
                        <li>Google hesabınızda 2 Faktörlü Doğrulama açık olmalı</li>
                        <li>Google Hesap Ayarları → Güvenlik → Uygulama Şifreleri</li>
                        <li>"Uygulama seç" → "Diğer" → İsim girin → "Oluştur"</li>
                        <li>Oluşturulan 16 haneli şifreyi buraya girin</li>
                      </ol>
                      <a 
                        href="https://myaccount.google.com/apppasswords" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-2 text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Uygulama Şifresi Oluştur
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gmail_email">Gmail Adresi</Label>
                  <Input
                    id="gmail_email"
                    type="email"
                    value={formData.gmail_email}
                    onChange={(e) => setFormData({ ...formData, gmail_email: e.target.value })}
                    placeholder="ornek@gmail.com"
                    data-testid="gmail-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gmail_app_password">
                    Uygulama Şifresi
                    {true && (
                      <span className={`text-xs ml-2 ${savedPw.gmail ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="gmail-password-state">{savedPw.gmail ? '(Kayıtlı)' : '(Kayıtlı şifre yok – girip kaydedin)'}</span>
                    )}
                  </Label>
                  <Input
                    id="gmail_app_password"
                    type="password"
                    value={formData.gmail_app_password}
                    onChange={(e) => setFormData({ ...formData, gmail_app_password: e.target.value })}
                    placeholder={savedPw.gmail ? 'Kayıtlı – değiştirmek için yeni şifre yazın' : 'xxxx xxxx xxxx xxxx'}
                    data-testid="gmail-password-input"
                  />
                  <p className="text-xs text-slate-500">16 haneli uygulama şifresi (boşluklar olmadan)</p>
                </div>

                <div className="space-y-2 pt-2 border-t">
                  <Label htmlFor="gmail_sender_name">Gönderici Adı <span className="font-normal text-slate-400">(gelen kutusunda görünür; boşsa "İSG Permit")</span></Label>
                  <Input
                    id="gmail_sender_name"
                    value={formData.gmail_sender_name}
                    onChange={(e) => setFormData({ ...formData, gmail_sender_name: e.target.value })}
                    placeholder="İSG Permit"
                    data-testid="gmail-sender-name-input"
                  />
                </div>

                <div className="flex justify-end pt-2">
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => handleTestConnection('gmail')}
                    disabled={testingGmail || !hasExistingSettings}
                    data-testid="test-gmail-btn"
                  >
                    {testingGmail ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Test Ediliyor...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Gmail Bağlantısını Test Et
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Actions */}
        <div className="flex items-center gap-3 mt-6">
          <Button 
            type="submit" 
            className="bg-slate-900 hover:bg-slate-800"
            disabled={saving}
            data-testid="save-email-settings-btn"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Kaydediliyor...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Ayarları Kaydet
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EmailSettingsPage;
